<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import PlanMapTimelineRouteRow from "./PlanMapTimelineRouteRow.vue";
import ArgonSearcherSimplified from "@/components/searcher/ArgonSearcherSimplified.vue";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";
import { formatAlertsText, normalizeAlerts } from "../../../../../utils/formatters";
const props = defineProps({
  routes: {
    type: Array,
    default() {
      return [];
    },
  },
});
const emit = defineEmits(["expanded-change", "visible-routes-change", "height-change"]);

const ROUTE_FALLBACK_COLORS = Object.freeze([
  "#55a7ff",
  "#16c47f",
  "#ff9f43",
  "#b15cff",
  "#ff4d8d",
  "#06b6d4",
  "#ffd166",
  "#95a1b2",
]);
const DEFAULT_TIMELINE_START_MINUTES = 7 * 60;
const DEFAULT_TIMELINE_END_MINUTES = 21 * 60;
const PANEL_COLLAPSED_HEIGHT_DESKTOP = 172;
const PANEL_COLLAPSED_HEIGHT_COMPACT = 160;
const PANEL_EXPANDED_RATIO_DESKTOP = 0.72;
const PANEL_EXPANDED_RATIO_COMPACT = 0.78;
const PANEL_EXPANDED_MAX_HEIGHT_DESKTOP = 620;
const PANEL_EXPANDED_MAX_HEIGHT_COMPACT = 560;
const PANEL_MIN_VISIBLE_MAP_HEIGHT_DESKTOP = 220;
const PANEL_MIN_VISIBLE_MAP_HEIGHT_COMPACT = 176;
const PANEL_DRAG_THRESHOLD_PX = 5;
const ROUTE_ROWS_SCROLL_THRESHOLD = 7;

function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function toSafeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeStatusKey(value) {
  return toSafeString(value).toUpperCase().replace(/[\s-]+/g, "_");
}

function parseOrder(value, fallbackValue) {
  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed > 0) return parsed;
  return fallbackValue;
}

function parseClockToMinutes(clockValue) {
  const text = toSafeString(clockValue);
  if (!text) return null;
  const match = text.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function parseDurationSeconds(value) {
  const normalized = Number.parseFloat(value);
  if (!Number.isFinite(normalized) || normalized < 0) return 0;
  return normalized;
}

function formatRouteTimeWindowLabel(value) {
  if (typeof value === "string") {
    return toSafeString(value) || "-";
  }

  if (!value || typeof value !== "object") {
    return "-";
  }

  const start = toSafeString(value.start_time || value.start);
  const end = toSafeString(value.end_time || value.end);
  if (start && end) {
    return `${start}-${end}`;
  }

  return start || end || "-";
}

function formatRouteTotalTimeLabel(value) {
  const totalSeconds = Number.parseFloat(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return "0 min";
  }

  return `${Math.round(totalSeconds / 60)} min`;
}

function formatMinutesAsClock(totalMinutes) {
  const normalized = Number.isFinite(totalMinutes) ? totalMinutes : 0;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function normalizeTimeWindow(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const start = toSafeString(value.start_time || value.start);
    const end = toSafeString(value.end_time || value.end);

    return {
      label: start && end ? `${start} - ${end}` : start || end || "-",
      startMinutes: parseClockToMinutes(start),
      endMinutes: parseClockToMinutes(end),
    };
  }

  const text = toSafeString(value);
  if (!text) return { label: "-", startMinutes: null, endMinutes: null };

  const matches = [...text.matchAll(/(\d{1,2}:\d{2})/g)];
  const startClock = matches[0]?.[1] || "";
  const endClock = matches[1]?.[1] || "";

  return {
    label: text.replace(/\s*-\s*/g, " - "),
    startMinutes: parseClockToMinutes(startClock),
    endMinutes: parseClockToMinutes(endClock),
  };
}

function getRouteStartMinutes(routeItem) {
  const routeWindow = normalizeTimeWindow(routeItem?.time_window);
  if (Number.isFinite(routeWindow.startMinutes)) return routeWindow.startMinutes;

  const fallbackCandidates = [
    routeItem?.start_time,
    routeItem?.vehicle?.start_time,
    routeItem?.vehicle?.time_window,
  ];

  for (const candidate of fallbackCandidates) {
    const parsed = normalizeTimeWindow(candidate);
    if (Number.isFinite(parsed.startMinutes)) return parsed.startMinutes;
  }

  return null;
}

function getRouteColor(routeItem, index) {
  const directColor = toSafeString(routeItem?.route_color_hex || routeItem?.vehicle?.route_color_hex);
  if (directColor) return directColor;
  return ROUTE_FALLBACK_COLORS[index % ROUTE_FALLBACK_COLORS.length];
}

function getRouteVehicleId(routeItem) {
  return toSafeString(routeItem?.vehicle_id);
}

function getRoutePlate(routeItem) {
  return toSafeString(
    routeItem?.plate ||
      routeItem?.vehicle_plate ||
      routeItem?.vehicle?.plate ||
      routeItem?.vehicle?.license_plate ||
      routeItem?.license_plate
  );
}

function getStopStatusMeta(status) {
  const normalized = normalizeStatusKey(status);
  const stopStatus = ESTATUS_ENUM.stop?.[normalized];
  if (stopStatus) {
    return {
      label: stopStatus.label,
      tone: stopStatus.type,
    };
  }

  return {
    label: normalized || "Pendiente",
    tone: "pendiente",
  };
}

function getStopAddress(stopItem) {
  const normalizedAddress = toSafeString(stopItem?.address_normalized?.address);
  if (normalizedAddress) return normalizedAddress;
  const rawAddress = stopItem?.address;
  if (typeof rawAddress === "string") return toSafeString(rawAddress);
  if (rawAddress && typeof rawAddress === "object") {
    return toSafeString(rawAddress.text) || toSafeString(rawAddress.address) || toSafeString(rawAddress.street);
  }
  return "-";
}
const routeVisibilityState = ref({});
const searchQuery = ref("");
const isExpanded = ref(false);
const panelHeight = ref(PANEL_COLLAPSED_HEIGHT_DESKTOP);
const isDraggingPanel = ref(false);
const activePointerId = ref(null);
const dragStartY = ref(0);
const dragStartHeight = ref(PANEL_COLLAPSED_HEIGHT_DESKTOP);
const didDragInGesture = ref(false);
const suppressNextHandleClick = ref(false);
const isCompactViewport = ref(false);
const panelRootRef = ref(null);
const activeTooltip = ref({
  html: "",
  left: 0,
  top: 0,
  visible: false,
});

function showTimelineTooltip(payload) {
  const anchorEl = payload?.anchorEl;
  const html = toSafeString(payload?.html);
  const panelEl = panelRootRef.value;
  if (!anchorEl || !panelEl || !html) return;

  const panelRect = panelEl.getBoundingClientRect();
  const anchorRect = anchorEl.getBoundingClientRect();
  activeTooltip.value = {
    html,
    left: anchorRect.left - panelRect.left + anchorRect.width / 2,
    top: anchorRect.top - panelRect.top - 10,
    visible: true,
  };
}

function hideTimelineTooltip() {
  activeTooltip.value.visible = false;
}

function onSearchQueryChange(value) {
  searchQuery.value = toSafeString(value);
}

const panelInlineStyle = computed(() => {
  const safeHeight = Math.max(0, Math.round(panelHeight.value || getCollapsedPanelHeight()));
  return {
    flexBasis: `${safeHeight}px`,
    minHeight: `${safeHeight}px`,
  };
});

const normalizedRoutes = computed(() => {
  return toSafeArray(props.routes).map((routeItem, routeIndex) => {
    const routeId = toSafeString(routeItem?._id || routeItem?.id || `route_${routeIndex + 1}`);
    const routeName = toSafeString(routeItem?.name || routeItem?.vehicle || "Ruta");
    const routeColor = getRouteColor(routeItem, routeIndex);
    const routeStartMinutes = getRouteStartMinutes(routeItem);
    let accumulatedRouteSeconds = 0;

    const stops = toSafeArray(routeItem?.routes)
      .map((stopItem, stopIndex) => {
        const fallbackOrder = stopIndex + 1;
        const order = parseOrder(stopItem?.order, fallbackOrder);
        const stopId = toSafeString(stopItem?._id || stopItem?.stop_id || `${routeId}_stop_${fallbackOrder}`);
        const statusMeta = getStopStatusMeta(stopItem?.status);
        const contact = stopItem.contact.name;
        const timeWindowPrimary = normalizeTimeWindow(stopItem?.time_window_1 || stopItem?.time_window);
        const timeWindowSecondary = normalizeTimeWindow(stopItem?.time_window_2);
        const key = `${routeId}::${stopId}`;
        const stopDurationSeconds = parseDurationSeconds(stopItem?.total_time);
        const timelineMinutes = Number.isFinite(routeStartMinutes)
          ? routeStartMinutes + accumulatedRouteSeconds / 60
          : timeWindowPrimary.startMinutes;

        accumulatedRouteSeconds += stopDurationSeconds;

        return {
          key,
          id: stopId,
          order,
          orderLabel: String(order),
          statusLabel: statusMeta.label,
          statusTone: statusMeta.tone,
          contact,
          address: getStopAddress(stopItem),
          timeWindow1Label: timeWindowPrimary.label,
          timeWindow2Label: timeWindowSecondary.label,
          startMinutes: timelineMinutes,
          endMinutes: Number.isFinite(timelineMinutes) ? timelineMinutes + stopDurationSeconds / 60 : timeWindowPrimary.endMinutes,
          stopDurationSeconds,
          timelineIndex: 0,
          timelineTotal: 0,
        };
      })
      .sort((first, second) => first.order - second.order);

    stops.forEach((stopItem, index) => {
      stopItem.timelineIndex = index;
      stopItem.timelineTotal = stops.length;
    });

    return {
      id: routeId,
      name: routeName,
      color: routeColor,
      vehicleId: getRouteVehicleId(routeItem),
      plate: getRoutePlate(routeItem),
      stopCount: String(stops.length),
      routeDistance: `${toSafeString(routeItem?.km ?? routeItem?.distance) || "0"} km`,
      routeTimeWindow: formatRouteTimeWindowLabel(routeItem?.time_window),
      routeTotalTime: formatRouteTotalTimeLabel(routeItem?.total_time),
      warningText: formatAlertsText(routeItem?.alerts),
      alerts: normalizeAlerts(routeItem?.alerts),
      stops,
    };
  });
});

const totalStops = computed(() =>
  normalizedRoutes.value.reduce((counter, routeItem) => counter + routeItem.stops.length, 0)
);


const normalizedSearchTerm = computed(() => toSafeString(searchQuery.value).toLowerCase());

const filteredRoutes = computed(() => {
  const query = normalizedSearchTerm.value;
  if (!query) return normalizedRoutes.value;

  return normalizedRoutes.value.filter((routeItem) => {
    const routeName = toSafeString(routeItem?.name).toLowerCase();
    const routeVehicleId = toSafeString(routeItem?.vehicleId).toLowerCase();
    const routePlate = toSafeString(routeItem?.plate).toLowerCase();

    return (
      routeName.includes(query) ||
      routeVehicleId.includes(query) ||
      routePlate.includes(query)
    );
  });
});

const hasFilteredRoutes = computed(() => filteredRoutes.value.length > 0);
const shouldLimitRouteRowsHeight = computed(
  () => !isExpanded.value && filteredRoutes.value.length > ROUTE_ROWS_SCROLL_THRESHOLD
);
const visibleRoutesCount = computed(() =>
  normalizedRoutes.value.reduce((counter, routeItem) => counter + (isRouteVisible(routeItem.id) ? 1 : 0), 0)
);
const isAllRoutesVisible = computed(
  () => normalizedRoutes.value.length > 0 && normalizedRoutes.value.every((routeItem) => isRouteVisible(routeItem.id))
);

const timelineBounds = computed(() => {
  return {
    startMinutes: DEFAULT_TIMELINE_START_MINUTES,
    endMinutes: DEFAULT_TIMELINE_END_MINUTES,
    duration: DEFAULT_TIMELINE_END_MINUTES - DEFAULT_TIMELINE_START_MINUTES,
  };
});

function getMinuteOffsetPercent(minutes) {
  const bounds = timelineBounds.value;
  if (!Number.isFinite(minutes)) return 0;
  const raw = ((minutes - bounds.startMinutes) * 100) / bounds.duration;
  if (raw < 0) return 0;
  if (raw > 100) return 100;
  return raw;
}

const timelineHours = computed(() => {
  const result = [];
  for (let minute = timelineBounds.value.startMinutes; minute <= timelineBounds.value.endMinutes; minute += 60) {
    result.push({
      minute,
      label: formatMinutesAsClock(minute),
      leftPercent: getMinuteOffsetPercent(minute),
    });
  }
  return result;
});

function getStopLeftPercent(stopItem) {
  if (Number.isFinite(stopItem?.startMinutes)) {
    return getMinuteOffsetPercent(stopItem.startMinutes);
  }

  if (!Number.isInteger(stopItem?.timelineTotal) || stopItem.timelineTotal <= 1) {
    return 0;
  }

  const ratio = (stopItem.timelineIndex * 100) / (stopItem.timelineTotal - 1);
  if (ratio < 0) return 0;
  if (ratio > 100) return 100;
  return ratio;
}

function getStopStyle(stopItem) {
  const leftPercent = getStopLeftPercent(stopItem);
  return {
    left: `clamp(18px, ${leftPercent}%, calc(100% - 18px))`,
  };
}

function isRouteVisible(routeId) {
  return routeVisibilityState.value[routeId] !== false;
}

function toggleRouteVisibility(routeId) {
  routeVisibilityState.value = {
    ...routeVisibilityState.value,
    [routeId]: !isRouteVisible(routeId),
  };
  emitVisibleRoutesChange();
}

function getVisibleStops(routeItem) {
  return isRouteVisible(routeItem?.id) ? routeItem?.stops || [] : [];
}

function getVisibleRoutesPayload() {
  const visibleRoutes = normalizedRoutes.value.filter((routeItem) => isRouteVisible(routeItem?.id));
  const routeIds = visibleRoutes.map((routeItem) => toSafeString(routeItem?.id)).filter(Boolean);
  const vehicleIds = [...new Set(visibleRoutes.map((routeItem) => toSafeString(routeItem?.vehicleId)).filter(Boolean))];

  return {
    routeIds,
    routeIdsCsv: routeIds.join(","),
    vehicleIds,
    vehicleIdsCsv: vehicleIds.join(","),
  };
}

function emitVisibleRoutesChange() {
  emit("visible-routes-change", getVisibleRoutesPayload());
}

function setAllRoutesVisibility(isVisible) {
  const nextState = {};
  normalizedRoutes.value.forEach((routeItem) => {
    nextState[routeItem.id] = Boolean(isVisible);
  });
  routeVisibilityState.value = nextState;

  emitVisibleRoutesChange();
}

function onVisibilityModeChange(mode) {
  if (mode === "all") {
    setAllRoutesVisibility(true);
    return;
  }
  if (mode === "none") {
    setAllRoutesVisibility(false);
  }
}

function getIsCompactViewport() {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 1200;
}

function getCollapsedPanelHeight() {
  return isCompactViewport.value ? PANEL_COLLAPSED_HEIGHT_COMPACT : PANEL_COLLAPSED_HEIGHT_DESKTOP;
}

function getExpandedPanelHeight() {
  if (typeof window === "undefined") {
    return isCompactViewport.value ? PANEL_EXPANDED_MAX_HEIGHT_COMPACT : PANEL_EXPANDED_MAX_HEIGHT_DESKTOP;
  }

  const viewportHeight = window.innerHeight;
  const maxHeight = isCompactViewport.value ? PANEL_EXPANDED_MAX_HEIGHT_COMPACT : PANEL_EXPANDED_MAX_HEIGHT_DESKTOP;
  const ratio = isCompactViewport.value ? PANEL_EXPANDED_RATIO_COMPACT : PANEL_EXPANDED_RATIO_DESKTOP;
  const minVisibleMapHeight = isCompactViewport.value
    ? PANEL_MIN_VISIBLE_MAP_HEIGHT_COMPACT
    : PANEL_MIN_VISIBLE_MAP_HEIGHT_DESKTOP;
  const parentHeight = panelRootRef.value?.parentElement?.clientHeight || viewportHeight;
  const availableHeight = Math.max(getCollapsedPanelHeight(), parentHeight - minVisibleMapHeight);
  const computedHeight = Math.min(viewportHeight * ratio, maxHeight, availableHeight);
  return Math.max(getCollapsedPanelHeight(), Math.round(computedHeight));
}

function getSnapPoints() {
  const collapsedHeight = getCollapsedPanelHeight();
  const expandedHeight = getExpandedPanelHeight();
  return [collapsedHeight, expandedHeight].sort((first, second) => first - second);
}

function clampPanelHeight(nextHeight) {
  const snapPoints = getSnapPoints();
  const minHeight = snapPoints[0];
  const maxHeight = snapPoints[snapPoints.length - 1];
  if (!Number.isFinite(nextHeight)) return minHeight;
  if (nextHeight < minHeight) return minHeight;
  if (nextHeight > maxHeight) return maxHeight;
  return nextHeight;
}


function syncPanelHeightWithState() {
  panelHeight.value = isExpanded.value ? getExpandedPanelHeight() : getCollapsedPanelHeight();
  emit("height-change", panelHeight.value);
}

function setExpandedState(nextExpanded, shouldEmit = true) {
  isExpanded.value = Boolean(nextExpanded);
  syncPanelHeightWithState();
  if (shouldEmit) emit("expanded-change", isExpanded.value);
}

function toggleExpanded() {
  setExpandedState(!isExpanded.value);
}

function onHandleClick() {
  if (suppressNextHandleClick.value) {
    suppressNextHandleClick.value = false;
    return;
  }
  toggleExpanded();
}

function onHandlePointerDown(event) {
  if (!event?.isPrimary) return;
  if (event.pointerType === "mouse" && event.button !== 0) return;
  if (activePointerId.value !== null) return;

  activePointerId.value = event.pointerId;
  dragStartY.value = event.clientY;
  dragStartHeight.value = panelHeight.value;
  didDragInGesture.value = false;

  const target = event.currentTarget;
  if (target instanceof Element && target.setPointerCapture) {
    target.setPointerCapture(event.pointerId);
  }
}

function onHandlePointerMove(event) {
  if (activePointerId.value !== event.pointerId) return;

  const deltaY = event.clientY - dragStartY.value;
  if (!didDragInGesture.value && Math.abs(deltaY) < PANEL_DRAG_THRESHOLD_PX) return;

  if (!didDragInGesture.value) {
    didDragInGesture.value = true;
    isDraggingPanel.value = true;
  }

  panelHeight.value = clampPanelHeight(dragStartHeight.value - deltaY);
  emit("height-change", panelHeight.value);
  event.preventDefault();
}

function resetDragState() {
  activePointerId.value = null;
  didDragInGesture.value = false;
  isDraggingPanel.value = false;
}

function finishHandlePointer(event) {
  if (activePointerId.value !== event.pointerId) return;

  const target = event.currentTarget;
  if (target instanceof Element && target.releasePointerCapture) {
    try {
      target.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // No-op: pointer may already be released.
    }
  }

  if (didDragInGesture.value) {
    isExpanded.value = panelHeight.value > getCollapsedPanelHeight();
    emit("expanded-change", isExpanded.value);
    suppressNextHandleClick.value = true;
  }

  resetDragState();
}

function onHandlePointerUp(event) {
  finishHandlePointer(event);
}

function onHandlePointerCancel(event) {
  finishHandlePointer(event);
}

function onWindowResize() {
  isCompactViewport.value = getIsCompactViewport();
  if (!isDraggingPanel.value) {
    syncPanelHeightWithState();
  }
}

watch(
  () => normalizedRoutes.value,
  (routesValue) => {
    const nextState = {};
    routesValue.forEach((routeItem) => {
      nextState[routeItem.id] =
        routeVisibilityState.value[routeItem.id] === undefined ? true : routeVisibilityState.value[routeItem.id];
    });
    routeVisibilityState.value = nextState;
  },
  { immediate: true }
);

onMounted(() => {
  isCompactViewport.value = getIsCompactViewport();
  syncPanelHeightWithState();
  window.addEventListener("resize", onWindowResize);
  emit("expanded-change", isExpanded.value);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onWindowResize);
});
</script>

<template>
  <section ref="panelRootRef" class="map-timeline-panel" :class="{ 'is-expanded': isExpanded, 'is-dragging': isDraggingPanel }" :style="panelInlineStyle">
    <button
      type="button"
      class="timeline-expand-toggle"
      :class="{ 'is-active': isDraggingPanel }"
      :title="isExpanded ? 'Contraer panel' : 'Expandir panel'"
      @click="onHandleClick"
      @pointerdown="onHandlePointerDown"
      @pointermove="onHandlePointerMove"
      @pointerup="onHandlePointerUp"
      @pointercancel="onHandlePointerCancel"
      @dragstart.prevent
    >
      <span class="timeline-expand-grip" aria-hidden="true"></span>
      <span class="timeline-expand-accessible">{{ isExpanded ? "Contraer panel" : "Expandir panel" }}</span>
    </button>

    <header class="timeline-summary">
      <div class="summary-main-row">
        <div class="summary-overview">
          <div class="summary-stats">
            <span class="summary-stat-pill">{{ normalizedRoutes.length }} rutas</span>
            <span class="summary-stat-pill">{{ totalStops }} paradas</span>
            <span class="summary-stat-pill is-active">{{ visibleRoutesCount }} visibles</span>
          </div>
        </div>

        <div class="timeline-summary-controls">
          <argon-searcher-simplified
            class="ms-auto timeline-search"
            icon="fa fa-search text-xxs"
            placeholder="Buscar vehículo..."
            width="min(184px, 32vw)"
            @search="onSearchQueryChange"
          />

          <div class="summary-toggle-group" role="group" aria-label="Visibilidad de rutas">
            <button
              type="button"
              class="summary-toggle-btn"
              :class="{ 'is-selected': isAllRoutesVisible }"
              title="Mostrar todas las rutas"
              @click="onVisibilityModeChange('all')"
            >
              <i class="fa-regular fa-eye" aria-hidden="true"></i>
              <span>Todos</span>
            </button>

            <button
              type="button"
              class="summary-toggle-btn"
              :class="{ 'is-selected': visibleRoutesCount === 0 }"
              title="Ocultar todas las rutas"
              @click="onVisibilityModeChange('none')"
            >
              <i class="fa-regular fa-eye-slash" aria-hidden="true"></i>
              <span>Ocultar</span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <div v-if="normalizedRoutes.length === 0" class="timeline-empty">
      Sin rutas disponibles para mostrar.
    </div>

    <div v-else-if="!hasFilteredRoutes" class="timeline-empty">
      No hay coincidencias para "{{ searchQuery }}".
    </div>

    <template v-else>
      <div
        class="timeline-scroll-shell"
        :class="{ 'has-route-scroll-limit': shouldLimitRouteRowsHeight }"
      >
        <div class="timeline-grid-head">
          <div class="scale-left">
            <span class="scale-caption">Rutas</span>
          </div>
          <div class="scale-right">
            <div
              v-for="hourItem in timelineHours"
              :key="`scale_${hourItem.minute}`"
              class="hour-marker"
              :class="{
                'is-start': hourItem.minute === timelineBounds.startMinutes,
                'is-end': hourItem.minute === timelineBounds.endMinutes,
              }"
              :style="{ left: `${hourItem.leftPercent}%` }"
            >
              <span class="hour-tick" aria-hidden="true"></span>
              <span class="hour-label">{{ hourItem.label }}</span>
            </div>
          </div>
        </div>

        <div class="timeline-rows">
          <plan-map-timeline-route-row
            v-for="routeItem in filteredRoutes"
            :key="routeItem.id"
            :route-item="routeItem"
            :hour-items="timelineHours"
            :is-visible="isRouteVisible(routeItem.id)"
            :visible-stops="getVisibleStops(routeItem)"
            :stop-style-resolver="getStopStyle"
            @toggle-visibility="toggleRouteVisibility"
            @tooltip-show="showTimelineTooltip"
            @tooltip-hide="hideTimelineTooltip"
          />
        </div>
      </div>
    </template>

    <div
      v-if="activeTooltip.visible"
      class="timeline-panel-tooltip"
      :style="{ left: `${activeTooltip.left}px`, top: `${activeTooltip.top}px` }"
      v-html="activeTooltip.html"
    ></div>
  </section>
</template>

<style scoped lang="scss">
@use "../../../components/planner-toggle-shared";
.map-timeline-panel {
  --timeline-left-col-width: 140px;
  background: #ffffff;
  border-top: 1px solid #dbe2ee;
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.06);
  color: var(--color-text-primary);
  display: flex;
  flex: 0 0 172px;
  flex-direction: column;
  min-height: 172px;
  overflow: visible;
  padding: 0 8px 8px;
  position: relative;
  transition: min-height 0.22s ease, flex-basis 0.22s ease;
}

.map-timeline-panel.is-dragging {
  transition: none;
}

.map-timeline-panel.is-expanded {
  flex-basis: min(72dvh, 620px);
  min-height: min(72dvh, 620px);
}

.timeline-expand-toggle {
  align-items: center;
  background: rgba(255, 255, 255, 0.98);
  border: none;
  border-radius: 999px;
  box-shadow: none;
  color: var(--color-text-secondary);
  cursor: grab;
  display: inline-flex;
  gap: 0;
  height: 18px;
  justify-content: center;
  left: 50%;
  padding: 0 10px;
  position: absolute;
  top: 0;
  touch-action: none;
  transform: translateX(-50%);
  transition: background 0.14s ease, box-shadow 0.14s ease, color 0.14s ease, border-color 0.14s ease;
  user-select: none;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  -webkit-user-select: none;
  width: min(280px, calc(100% - 32px));
  z-index: 6;
}

.timeline-expand-toggle:hover {
  background: #f8fafc;
  border-color: rgba(148, 163, 184, 0.62);
  box-shadow: none;
  color: #475569;
}

.timeline-expand-toggle:focus-visible {
  box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.16);
  outline: none;
}

.timeline-expand-toggle:active,
.map-timeline-panel.is-dragging .timeline-expand-toggle {
  cursor: grabbing;
}

.timeline-expand-toggle.is-active {
  background: #f8fafc;
  border-color: rgba(94, 114, 228, 0.44);
  box-shadow: none;
  color: var(--color-text-primary);
}

.timeline-expand-grip {
  align-items: center;
  display: inline-flex;
  flex: 1 1 auto;
  justify-content: center;
}

.timeline-expand-grip::before {
  background-image:
    linear-gradient(currentColor, currentColor),
    linear-gradient(currentColor, currentColor),
    linear-gradient(currentColor, currentColor);
  background-position: center 0px, center 4px, center 8px;
  background-repeat: no-repeat;
  background-size: 18px 1.5px, 28px 1.5px, 18px 1.5px;
  content: "";
  display: block;
  height: 10px;
  opacity: 0.46;
  width: 30px;
}

.timeline-expand-accessible {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.timeline-summary {
  align-items: center;
  border-bottom: 1px solid #dbe2ee;
  display: flex;
  justify-content: center;
  margin: 0 -8px 6px;
  padding: 8px 14px;
}

.summary-main-row {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  min-width: 0;
  width: 100%;
}

.summary-overview {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}

.summary-stats {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

.summary-stat-pill {
  align-items: center;
  background: transparent;
  border: 1px solid rgba(203, 213, 225, 0.9);
  border-radius: 999px;
  color: var(--color-text-secondary);
  display: inline-flex;
  font-size: 0.65rem;
  font-weight: 500;
  line-height: 1;
  min-height: 22px;
  padding: 0 8px;
  white-space: nowrap;
}

.summary-stat-pill.is-active {
  background: rgba(94, 114, 228, 0.07);
  border-color: rgba(94, 114, 228, 0.22);
  color: var(--bs-primary, #5e72e4);
}

.timeline-summary-controls {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 6px;
  justify-content: flex-end;
  min-width: 0;
}

.timeline-search {
  flex: 0 1 auto;
}

.timeline-search:deep(.searcher-simplified) {
  background: #ffffff;
  border: 1px solid #dbe2ee !important;
  border-radius: 7px;
  min-height: 28px;
  padding-left: 10px !important;
  transition: border-color 0.14s ease, box-shadow 0.14s ease;
}

.timeline-search:deep(.searcher-simplified:hover) {
  border-color: #c5d0de !important;
}

.timeline-search:deep(.searcher-simplified:focus-within) {
  border-color: rgba(94, 114, 228, 0.4) !important;
  box-shadow: 0 0 0 3px rgba(94, 114, 228, 0.08);
}

.timeline-search:deep(.searcher-simplified i) {
  color: #94a3b8;
}

.timeline-search:deep(.simplified-input) {
  color: var(--color-text-primary);
  font-size: 0.67rem;
  margin-left: 4px;
  margin-top: 0 !important;
  padding: 0.15rem 0.5rem 0.15rem 0.25rem;
}

.timeline-search:deep(.simplified-input::placeholder) {
  color: #94a3b8;
}

.timeline-search:deep(.fa-times) {
  color: #94a3b8;
  transition: color 0.14s ease;
}

.timeline-search:deep(.fa-times:hover) {
  color: var(--color-text-secondary);
}

.summary-toggle-group {
  @extend .planner-summary-toggle-group;
}

.summary-toggle-btn {
  @extend .planner-summary-toggle-btn;
}

.timeline-empty {
  align-items: center;
  color: var(--color-text-secondary);
  display: flex;
  font-size: 0.72rem;
  height: 100%;
  justify-content: center;
  text-align: center;
}

.timeline-grid-head {
  align-items: stretch;
  background: #ffffff;
  border-bottom: 1px solid #dbe2ee;
  display: grid;
  gap: 6px;
  grid-template-columns: var(--timeline-left-col-width) minmax(0, 1fr);
  margin: -6px -4px 6px;
  min-height: 28px;
  padding: 2px 10px 6px;
  position: sticky;
  top: -6px;
  z-index: 12;
}

.scale-left {
  align-items: center;
  display: inline-flex;
  min-width: 0;
}

.scale-caption {
  color: var(--color-text-secondary);
  font-size: 0.60rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  line-height: 1;
  text-transform: uppercase;
}

.scale-right {
  min-height: 28px;
  overflow: hidden;
  position: relative;
}

.hour-marker {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 3px;
  justify-content: flex-start;
  position: absolute;
  top: 3px;
  transform: translateX(-50%);
}

.hour-tick {
  background: #a8b8c8;
  border-radius: 999px;
  display: block;
  height: 6px;
  margin: 0;
  width: 1px;
}

.hour-label {
  color: #6b7d92;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.005em;
  order: -1;
  white-space: nowrap;
}

.timeline-scroll-shell {
  background: #ffffff;
  border: 1px solid #dbe2ee;
  border-radius: 10px;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
  overflow-x: clip;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px 4px 4px;
  position: relative;
  scrollbar-color: rgba(148, 163, 184, 0.45) transparent;
  scrollbar-gutter: stable both-edges;
  scrollbar-width: thin;
  z-index: 1;
}

.hour-marker.is-start .hour-label {
  transform: translateX(50%);
}

.hour-marker.is-end .hour-label {
  transform: translateX(-50%);
}

.timeline-scroll-shell.has-route-scroll-limit {
  max-height: 286px;
}

.timeline-rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: fit-content;
  padding: 0;
  position: relative;
}

.timeline-panel-tooltip {
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  transform: translate(-50%, -100%);
  z-index: 20;
}

.timeline-panel-tooltip :deep(.map-stop-popup) {
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(203, 213, 225, 0.95);
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  color: #0f172a;
  max-width: 220px;
}

:deep(.timeline-scroll-shell::-webkit-scrollbar) {
  width: 7px;
}

:deep(.timeline-scroll-shell::-webkit-scrollbar-track) {
  background: rgba(226, 232, 240, 0.28);
  border-radius: 999px;
}

:deep(.timeline-scroll-shell::-webkit-scrollbar-thumb) {
  background: rgba(148, 163, 184, 0.42);
  border-radius: 999px;
}

:deep(.timeline-scroll-shell::-webkit-scrollbar-thumb:hover) {
  background: rgba(100, 116, 139, 0.56);
}

@media (max-width: 920px) {
  .map-timeline-panel {
    --timeline-left-col-width: 126px;
  }

  .map-timeline-panel {
    flex-basis: 160px;
    min-height: 160px;
  }

  .map-timeline-panel.is-expanded {
    flex-basis: min(78dvh, 560px);
    min-height: min(78dvh, 560px);
  }

  .summary-main-row {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }

  .summary-overview {
    gap: 6px;
  }

  .timeline-summary-controls {
    justify-content: space-between;
  }

  .summary-search-wrap {
    width: min(172px, 54vw);
  }
}
</style>
